import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { clearLocalStorage } from "../functions";
import { API, graphqlOperation } from "aws-amplify";


const kiosk_onCreateEventMark = /* GraphQL */ `
  subscription OnCreateEventMark($kioskId: String) {
    onCreateEventMark(filter: {
        entity: {
            eq: "kiosk"
        },
        entityId: {
            eq: $kioskId
        },
    }) {
        entity
        entityId
        topic
    }
  }
`;

const KioskContext = React.createContext({
    isKioskMode: false,
    kioskDomain: null,
    isKioskLoggedIn: false,
    setLoginKiosk: () => {},
    setLogoutKiosk: () => {},
    lastEvent: null,
    eventVersion: 0,
    clearLastEvent: () => {},
    isProcessingEvent: false,
    setIsProcessingEvent: () => {},
    inactivityWarningActive: false,
    setInactivityWarningActive: () => {},
    inactivityTimeoutMs: 300000,
    setInactivityTimeoutMs: () => {},
});

function KioskProvider({ children }) {
    // Inicializar isKioskMode desde sessionStorage o detectar por dominio/ruta
    const [isKioskMode, setIsKioskMode] = useState(() => {
        const storedKioskMode = sessionStorage.getItem('kioskMode') === 'true';
        if (storedKioskMode) {
            return true;
        }
        // Si no hay en sessionStorage, detectar por dominio o ruta
        const currentDomain = window.location.hostname;
        const currentPath = window.location.pathname;
        return currentDomain.includes('kiosk') || currentPath.includes('/kiosk');
    });
    
    const [kioskDomain, setKioskDomain] = useState(null);
    // Inicializar isKioskLoggedIn desde sessionStorage
    const [isKioskLoggedIn, setIsKioskLoggedIn] = useState(() => {
        return sessionStorage.getItem('isKioskLoggedIn') === 'true';
    });
    const subscriptionRef = useRef(null);
    const [lastEvent, setLastEvent] = useState(null);
    const [eventVersion, setEventVersion] = useState(0);
    const [isProcessingEvent, setIsProcessingEvent] = useState(false);
    const [inactivityWarningActive, setInactivityWarningActive] = useState(false);

    // Tiempo permitido de inactividad previo a que se muestre el cartel de inactividad
    const [inactivityTimeoutMs, setInactivityTimeoutMs] = useState(300000); // 5 minutos por defecto

    useEffect(() => {
        if (isKioskMode) {
            const currentDomain = window.location.hostname;
            setKioskDomain(currentDomain);
            // Almacenar flag en sessionStorage para persistencia en la sesión
            sessionStorage.setItem('kioskMode', 'true');
        } else {
            // Si salimos del modo kiosk, limpiar sessionStorage
            sessionStorage.removeItem('kioskMode');
        }
    }, [isKioskMode]);

    const setLoginKiosk = () => {
        setIsKioskLoggedIn(true);
        // Persistir el estado de login en sessionStorage
        sessionStorage.setItem('isKioskLoggedIn', 'true');
    };

    const setLogoutKiosk = () => {
        setIsKioskLoggedIn(false);
        // Limpiar el estado de login de sessionStorage
        sessionStorage.removeItem('isKioskLoggedIn');
    };

    useEffect(() => {
        let retryDelay = 1000;
        let active = true;
        let timeoutId = null;
    
        function startSubscription() {
            if (!active) return;
            subscriptionRef.current = API.graphql(
                graphqlOperation(kiosk_onCreateEventMark)
            ).subscribe({
                next: ({ value }) => {
                    if (!active) return; // ✅ Previene actualizaciones después del desmontaje
                    retryDelay = 1000;
                    const newEvent = value?.data?.onCreateEventMark;
                    setLastEvent(newEvent);
                    setEventVersion((v) => v + 1);
                },
                error: (err) => {
                    if (!active) return; // ✅ Previene reintento después del desmontaje
                    console.error("❌ Error en subscription:", err);
                    resubscribe();
                },
                complete: () => {
                    if (!active) return;
                    resubscribe();
                },
            });
        }
    
        function resubscribe() {
            if (!active) return;
            const delay = retryDelay;
            retryDelay = Math.min(retryDelay * 2, 30000);
            console.log(`🔄 Reintentando en ${delay / 1000}s...`);
    
            // ✅ Guardamos el ID del timeout para limpiarlo luego
            timeoutId = setTimeout(() => {
                if (active) {
                    startSubscription();
                }
            }, delay);
        }
    
        startSubscription();
    
        // ✅ Cleanup completo
        return () => {
            active = false;
            if (timeoutId) {
                clearTimeout(timeoutId); // ✅ Cancelamos el timeout pendiente
            }
            subscriptionRef.current?.unsubscribe();
        };
    }, []);

    const clearLastEvent = useCallback(() => {
        setLastEvent(null);
    }, []);

    const value = useMemo(() => ({
        isKioskMode,
        kioskDomain,
        isKioskLoggedIn,
        setLoginKiosk,
        setLogoutKiosk,
        lastEvent,
        eventVersion,
        clearLastEvent,
        isProcessingEvent,
        setIsProcessingEvent,
        inactivityWarningActive,
        setInactivityWarningActive,
        inactivityTimeoutMs,
        setInactivityTimeoutMs
    }), [isKioskMode, kioskDomain, isKioskLoggedIn, lastEvent, eventVersion, clearLastEvent, isProcessingEvent, inactivityWarningActive, inactivityTimeoutMs]);

    return (
        <KioskContext.Provider value={value}>
            {children}
        </KioskContext.Provider>
    );
}

export { KioskContext, KioskProvider };

